import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import SubmitButton from '../components/SubmitButton'
import ReviewReceiptCard, {
  ReviewReceiptCardProps,
} from '../components/ReviewReceiptCard'
import { VisitType } from '../constants/visitType'
import { reviewApi } from '../api/review'
import LoadingSpinner from '../components/LoadingSpinner'

const ReviewComplete: React.FC = () => {
  const navigate = useNavigate()
  const { reviewId } = useParams<{ reviewId: string }>()
  const [searchParams] = useSearchParams()
  const [reviewData, setReviewData] = useState<ReviewReceiptCardProps>({
    placeName: '',
    placeAddress: '',
    placeCategory: null,
    score: null,
    hideScore: false,
    dateDisplay: null,
    visitDate: null,
    createdAt: null,
    visitType: VisitType.STORE,
    reviewText: null,
    taggedFriends: [],
    minPricePerPerson: null,
    maxPricePerPerson: null,
    keywords: [],
  })

  /**
   * 방금 기록한 장소의 id.
   *
   * 랭킹 화면으로 넘어갈 때 함께 넘겨, 그 가게가 몇 등에 들어갔는지
   * 사용자가 직접 찾지 않아도 되게 한다. 예전에는 카테고리만 넘겨서
   * 목록 맨 위로 갔고, 본인 가게를 스크롤해 찾아야 했다.
   */
  const [placeId, setPlaceId] = useState<number | null>(null)

  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const loadReviewData = async () => {
      if (!reviewId) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const response = await reviewApi.getReviewDetail(parseInt(reviewId))
        if (response.review.place?.name) {
          setPlaceId(response.review.place.id ?? null)
          setReviewData({
            placeName: response.review.place.name,
            placeAddress: response.review.place.address || '',
            placeCategory: response.review.place.category
              ? response.review.place.category
              : null,
            score: response.review.score,
            hideScore: response.review.hide_score || false,
            dateDisplay: response.review.date_display ?? null,
            visitNumber: response.review.visit_number ?? null,
            visitDate: response.review.visit_date,
            createdAt: response.review.created_at,
            visitType: response.review.visit_type as VisitType,
            reviewText: response.review.review_text,
            taggedFriends: response.review.tagged_friends || [],
            minPricePerPerson: response.review.min_price_per_person,
            maxPricePerPerson: response.review.max_price_per_person,
            keywords: response.review.keywords || [],
          })
        } else {
          setLoadError('가게 정보를 불러오는데 실패했습니다')
        }
      } catch (error) {
        setLoadError('기록을 불러오는데 실패했습니다')
      } finally {
        setIsLoading(false)
      }
    }

    loadReviewData()
  }, [reviewId])

  const handleComplete = () => {
    const redirect = searchParams.get('redirect')

    /**
     * 랭킹 화면으로 가는 주소에 highlight를 붙인다.
     *
     * 랭킹 화면은 이 값을 보고 방금 기록한 가게로 스크롤하고 잠깐 강조한다.
     * redirect에도 붙여야 한다 — 랭킹에서 '수정'으로 들어오면 redirect가
     * 채워지는데, 예전에는 그 경우 highlight가 통째로 무시돼서
     * 정작 가장 필요한 상황(내가 몇 등이 됐나)에 아무것도 안 보였다.
     */
    const withHighlight = (url: string): string => {
      if (placeId === null || !url.includes('/main-ranking')) {
        return url
      }
      return `${url}${url.includes('?') ? '&' : '?'}highlight=${placeId}`
    }

    // 방금 리뷰한 장소의 카테고리(레스토랑/카페/술집) 랭킹 화면으로 이동
    const rankingPath = reviewData.placeCategory
      ? `/main-ranking?category=${encodeURIComponent(reviewData.placeCategory)}`
      : '/main-ranking'
    let target = redirect ? decodeURIComponent(redirect) : rankingPath

    /*
     * 기록 줄(가져오기 → 한 곳씩 기록)로 돌아갈 때만 '완료' 표시를 붙인다.
     *
     * 받는 쪽은 돌아온 길이 **완료인지 뒤로가기인지** 구분할 수 없었다.
     * 둘 다 /onboarding-rank 로 오기 때문이다. 그래서 뒤로 가려고 '<' 를
     * 눌러도 '다음 장소' 로 읽혀, 고른 곳들의 리뷰 화면이 줄줄이 지나간 뒤
     * 홈으로 튕겼다.
     *
     * 다른 곳(/main-ranking 등)으로 갈 때는 안 붙인다 — 그쪽은 이 값을 보지
     * 않으므로 주소가 달라질 이유가 없다.
     */
    if (target.startsWith('/onboarding-rank')) {
      target += (target.includes('?') ? '&' : '?') + 'rankStep=done'
    }

    // 임시 진단 — 어떤 주소로 넘기는지 남긴다. 원인을 잡으면 지운다.
    void import('../api/base/apiClient').then(({ default: client }) =>
      client
        .post(
          '/diagnostics/trace',
          {
            context: 'review-complete-nav',
            detail: {
              placeId,
              redirect,
              category: reviewData.placeCategory,
              target: withHighlight(target),
            },
          },
          { skipErrorHandler: true } as never
        )
        .catch(() => {})
    )

    // replace: 영수증 화면을 히스토리에서 제거 — 랭킹에서 뒤로가기 시 영수증 재노출 방지
    navigate(withHighlight(target), { replace: true })
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="h-full app-container flex flex-col items-center justify-center">
          <LoadingSpinner size={28} borderWidth={3} />
        </div>
      )
    }

    if (loadError) {
      return (
        <div className="h-full app-container flex flex-col items-center justify-center">
          <p className="font-pretendard font-regular text-16px text-textColorText2 text-center">
            {loadError}
          </p>
        </div>
      )
    }

    return (
      <div className="h-full app-container flex flex-col pb-32px min-h-0">
        {/* 영수증 카드 — 세로·가로 중앙 (내용이 길면 스크롤) */}
        <div
          className={`
            w-full flex-1 flex flex-col items-center justify-center
            overflow-y-auto scrollbar-hide min-h-0
          `}
        >
          <div className="w-full max-w-full px-33px py-40px flex justify-center">
            <div className="w-full max-w-[324px]">
              <ReviewReceiptCard {...reviewData} />
            </div>
          </div>
        </div>

        {/* 완료 버튼 */}
        <div className="w-full shrink-0 px-20px">
          <SubmitButton text="완료" onSubmit={handleComplete} />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-ivoryKey">
      {renderBody()}
    </div>
  )
}

export default ReviewComplete
